import { state } from "../state.js";
import { audioCtx, getBuffer } from "./context.js";

export let globalBgmVolume = 0.5;
let bgmSource: AudioBufferSourceNode | null = null;
let bgmGainNode: GainNode | null = null;
export let isBgmPlaying = false;
let synthBgmInterval: ReturnType<typeof setInterval> | null = null;

export function setBgmVolume(vol: number) {
  globalBgmVolume = vol;
  if (bgmGainNode) {
    bgmGainNode.gain.value = globalBgmVolume;
  }
}

let realisticBgmInterval: ReturnType<typeof setInterval> | null = null;

function playRealisticBgmLoop() {
  if (realisticBgmInterval) return;
  let step = 0;

  // Root note frequencies for the drone/chords: D, Bb, C, A
  const roots = [58.27, 73.42, 65.41, 55.00];
  // Horn melody notes: D3, F3, G3, A3, Bb3, A3, G3, F3
  const melody = [146.83, 174.61, 196.00, 220.00, 233.08, 220.00, 196.00, 174.61];

  realisticBgmInterval = setInterval(() => {
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;

    const sequenceStep = step % 16;
    const chordIndex = Math.floor(step / 8) % roots.length;
    const currentRoot = roots[chordIndex];

    // 1. Deep Bass Drone (re-triggered every 8 steps to maintain the rumble)
    if (sequenceStep === 0) {
      const droneOsc = audioCtx.createOscillator();
      droneOsc.type = "triangle";
      droneOsc.frequency.setValueAtTime(currentRoot, now);
      droneOsc.frequency.linearRampToValueAtTime(currentRoot * 0.98, now + 2.3);

      const droneFilter = audioCtx.createBiquadFilter();
      droneFilter.type = "lowpass";
      droneFilter.frequency.setValueAtTime(120, now);

      const droneGain = audioCtx.createGain();
      droneGain.gain.setValueAtTime(0.001, now);
      droneGain.gain.linearRampToValueAtTime(0.22 * globalBgmVolume, now + 0.5);
      droneGain.gain.exponentialRampToValueAtTime(0.001, now + 2.3);

      droneOsc.connect(droneFilter);
      droneFilter.connect(droneGain);
      droneGain.connect(audioCtx.destination);
      droneOsc.start(now);
      droneOsc.stop(now + 2.4);
    }

    // 2. Orchestral Timpani Drum Hit (on beat 1 and 3 of the bar: step 0, 8)
    if (sequenceStep === 0 || sequenceStep === 8) {
      // Noise burst for the impact rumble
      const timpBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 0.4, audioCtx.sampleRate);
      const timpData = timpBuffer.getChannelData(0);
      for (let i = 0; i < timpData.length; i++) {
        timpData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (audioCtx.sampleRate * 0.1));
      }
      const timpSource = audioCtx.createBufferSource();
      timpSource.buffer = timpBuffer;

      const timpFilter = audioCtx.createBiquadFilter();
      timpFilter.type = "lowpass";
      timpFilter.frequency.setValueAtTime(90, now);
      timpFilter.frequency.exponentialRampToValueAtTime(30, now + 0.3);
      timpFilter.Q.value = 4.0;

      const timpGain = audioCtx.createGain();
      timpGain.gain.setValueAtTime(0.28 * globalBgmVolume, now);
      timpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      timpSource.connect(timpFilter);
      timpFilter.connect(timpGain);
      timpGain.connect(audioCtx.destination);
      timpSource.start(now);
      timpSource.stop(now + 0.4);
    }

    // 3. Snare March (Military parade rimshots and rolls)
    const isSnareAccent = sequenceStep === 4 || sequenceStep === 12;
    const isSnareTap = sequenceStep === 2 || sequenceStep === 6 || sequenceStep === 10 || sequenceStep === 14;

    if (isSnareAccent || isSnareTap) {
      const snareLength = isSnareAccent ? 0.08 : 0.04;
      const snareVolume = (isSnareAccent ? 0.10 : 0.04) * globalBgmVolume;
      const snareBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * snareLength, audioCtx.sampleRate);
      const snareData = snareBuffer.getChannelData(0);
      for (let i = 0; i < snareData.length; i++) {
        snareData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (snareData.length * 0.35));
      }
      const snareSource = audioCtx.createBufferSource();
      snareSource.buffer = snareBuffer;

      const snareFilter = audioCtx.createBiquadFilter();
      snareFilter.type = "bandpass";
      snareFilter.frequency.setValueAtTime(isSnareAccent ? 1200 : 1500, now);
      snareFilter.Q.value = 1.8;

      const snareGain = audioCtx.createGain();
      snareGain.gain.setValueAtTime(snareVolume, now);
      snareGain.gain.exponentialRampToValueAtTime(0.001, now + snareLength);

      snareSource.connect(snareFilter);
      snareFilter.connect(snareGain);
      snareGain.connect(audioCtx.destination);
      snareSource.start(now);
      snareSource.stop(now + snareLength);
    }

    // 4. Cinematic Horn/Brass Swell (triggers every 16 steps)
    if (sequenceStep === 0) {
      const hornIndex = Math.floor(step / 16) % melody.length;
      const hornFreq = melody[hornIndex];

      const osc1 = audioCtx.createOscillator();
      const osc2 = audioCtx.createOscillator();

      osc1.type = "sawtooth";
      osc2.type = "sawtooth";

      osc1.frequency.setValueAtTime(hornFreq, now);
      osc2.frequency.setValueAtTime(hornFreq * 1.006, now); // slightly detuned

      const hornFilter = audioCtx.createBiquadFilter();
      hornFilter.type = "lowpass";
      hornFilter.frequency.setValueAtTime(100, now);
      hornFilter.frequency.exponentialRampToValueAtTime(700, now + 1.2);
      hornFilter.frequency.exponentialRampToValueAtTime(300, now + 2.2);

      const hornGain = audioCtx.createGain();
      hornGain.gain.setValueAtTime(0.001, now);
      hornGain.gain.linearRampToValueAtTime(0.06 * globalBgmVolume, now + 0.8);
      hornGain.gain.exponentialRampToValueAtTime(0.001, now + 2.2);

      osc1.connect(hornFilter);
      osc2.connect(hornFilter);
      hornFilter.connect(hornGain);
      hornGain.connect(audioCtx.destination);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 2.3);
      osc2.stop(now + 2.3);
    }

    step++;
  }, 300);
}

function stopRealisticBgmLoop() {
  if (realisticBgmInterval) {
    clearInterval(realisticBgmInterval);
    realisticBgmInterval = null;
  }
}

function playSynthBgm() {
  if (state.realisticEnabled) {
    playRealisticBgmLoop();
    return;
  }
  if (synthBgmInterval) return;
  let step = 0;

  let notes: number[];
  let waveType: OscillatorType = "triangle";
  let tempo = 250;
  if (state.modernEnabled) {
    notes = [
      73.42, 73.42, 73.42, 73.42, 87.31, 87.31, 98.00, 98.00,
      73.42, 73.42, 73.42, 73.42, 110.00, 110.00, 98.00, 87.31
    ]; // D2, F2, G2, A2 cyber bass
    waveType = "sawtooth";
    tempo = 220;
  } else {
    notes = [
      110, 110, 130, 110, 165, 165, 147, 130,
      110, 110, 130, 110, 196, 196, 175, 165
    ];
  }

  synthBgmInterval = setInterval(() => {
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();

    if (state.modernEnabled) {
      osc.type = waveType;
      const lowpass = audioCtx.createBiquadFilter();
      lowpass.type = "lowpass";
      lowpass.frequency.setValueAtTime(150, now);

      osc.frequency.setValueAtTime(notes[step % notes.length], now);
      gain.gain.setValueAtTime(0.12 * globalBgmVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

      osc.connect(lowpass);
      lowpass.connect(gain);
    } else {
      osc.type = waveType;
      osc.frequency.setValueAtTime(notes[step % notes.length], now);
      gain.gain.setValueAtTime(0.18 * globalBgmVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

      osc.connect(gain);
    }

    gain.connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
    step++;
  }, tempo);
}

function stopSynthBgm() {
  if (state.realisticEnabled) {
    stopRealisticBgmLoop();
  }
  if (synthBgmInterval) {
    clearInterval(synthBgmInterval);
    synthBgmInterval = null;
  }
}

export function playBgm() {
  if (isBgmPlaying) return;

  if (state.battleCityEnabled || state.modernEnabled || state.realisticEnabled) {
    if (audioCtx.state === "suspended") audioCtx.resume();
    playSynthBgm();
    isBgmPlaying = true;
    return;
  }

  const buffer = getBuffer("bgm");
  if (!buffer) {
    // If called before the fetch completes, retry shortly
    setTimeout(playBgm, 500);
    return;
  }

  if (audioCtx.state === "suspended") audioCtx.resume();
  bgmSource = audioCtx.createBufferSource();
  bgmSource.buffer = buffer;
  bgmSource.loop = true;

  bgmGainNode = audioCtx.createGain();
  bgmGainNode.gain.value = globalBgmVolume;

  bgmSource.connect(bgmGainNode);
  bgmGainNode.connect(audioCtx.destination);
  bgmSource.start();
  isBgmPlaying = true;
}

export function pauseBgm() {
  if (!isBgmPlaying) return;
  if (state.battleCityEnabled || state.modernEnabled || state.realisticEnabled) {
    stopSynthBgm();
  } else if (bgmSource) {
    bgmSource.stop();
    bgmSource.disconnect();
    bgmSource = null;
  }
  isBgmPlaying = false;
}
